import { Link, useLocation } from "wouter";
import { APP_VERSION } from "@/lib/version";
import {
  Shield,
  Users,
  Key,
  Settings,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  FileSearch,
  Trophy,
  Sprout,
  DollarSign,
  LayoutTemplate,
  Database, Ticket } from "lucide-react";
import { cn } from "@/lib/utils";

interface NavItem {
  icon: React.ElementType;
  label: string;
  href: string;
  dot?: string;
  disabled?: boolean;
}

const INTELLIGENCE_ITEMS: NavItem[] = [
  { icon: Database, label: "Meta Intelligence", href: "/admin/meta-intel", dot: "#0EA5E9" },
  { icon: Trophy, label: "Winning Ads", href: "/admin/winning-ads", dot: "#F59E0B" },
  { icon: Sprout, label: "Ad Library Seeder", href: "/admin/ad-library-seeder", dot: "#10B981" },
  { icon: Users, label: "Talent Library", href: "/admin/talent-library", dot: "#EC4899" },
  { icon: LayoutTemplate, label: "Ad Templates", href: "/admin/ad-templates", dot: "#8B5CF6" },
  { icon: FileSearch, label: "Platform Docs", href: "/admin/platform-docs", dot: "#6366F1" },
];

const BILLING_ITEMS: NavItem[] = [
  { icon: DollarSign, label: "Pricing", href: "/admin/pricing", dot: "#10B981" },
  { icon: Ticket, label: "Promo Coupons", href: "/admin/coupons", dot: "#34D399" },
];

const DATA_ITEMS: NavItem[] = [
  { icon: Shield, label: "Connected Accounts", href: "/admin/accounts", dot: "#6366F1" },
  { icon: Users, label: "User Management", href: "/admin/users", dot: "#3B82F6" },
];

const SYSTEM_ITEMS: NavItem[] = [
  { icon: Key, label: "API Keys", href: "/admin/api-keys", dot: "#9CA3AF", disabled: true },
  { icon: Settings, label: "Settings", href: "/admin/settings", dot: "#6B7280", disabled: true },
];

const SECTIONS = [
  { label: "Intelligence", items: INTELLIGENCE_ITEMS },
  { label: "Billing", items: BILLING_ITEMS },
  { label: "Data", items: DATA_ITEMS },
  { label: "System", items: SYSTEM_ITEMS },
];

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export function AdminSidebar({ isCollapsed, onToggle }: AdminSidebarProps) {
  const [location] = useLocation();

  const isActive = (href: string) =>
    location === href || location.startsWith(href + "/");

  const NavItemEl = ({ item }: { item: NavItem }) => {
    const active = isActive(item.href);
    if (item.disabled) {
      return (
        <div
          className={cn(
            "flex items-center rounded-lg py-2.5 text-sm opacity-40 cursor-not-allowed pointer-events-none select-none",
            isCollapsed ? "justify-center px-2" : "gap-3 px-3"
          )}
          title={isCollapsed ? item.label : undefined}
        >
          <span className="relative flex-shrink-0 flex items-center gap-2">
            {item.dot && (
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.dot }} />
            )}
            {!item.dot && <item.icon className="h-5 w-5 stroke-[1.5] text-gray-500" />}
          </span>
          {!isCollapsed && (
            <>
              {item.dot && <item.icon className="h-4.5 w-4.5 stroke-[1.5] text-gray-400" />}
              <span className="text-gray-400 flex-1">{item.label}</span>
              <span className="text-[10px] font-medium bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-full">Soon</span>
            </>
          )}
        </div>
      );
    }

    return (
      <Link
        href={item.href}
        className={cn(
          "flex items-center rounded-lg py-2.5 text-sm transition-all duration-200 cursor-pointer group select-none",
          isCollapsed ? "justify-center px-2" : "gap-3 px-3",
          active
            ? "bg-white/10 text-white"
            : "text-gray-400 hover:text-white hover:bg-white/5"
        )}
        title={isCollapsed ? item.label : undefined}
      >
        <span className="relative flex-shrink-0 flex items-center gap-2">
          {item.dot && (
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.dot }} />
          )}
          {!item.dot && <item.icon className={cn("h-5 w-5 stroke-[1.5]", active ? "text-white" : "text-gray-500 group-hover:text-gray-300")} />}
        </span>
        {!isCollapsed && (
          <>
            {item.dot && <item.icon className={cn("h-4 w-4 stroke-[1.5]", active ? "text-white" : "text-gray-400 group-hover:text-gray-300")} />}
            <span className="flex-1">{item.label}</span>
          </>
        )}
      </Link>
    );
  };

  return (
    <div className="flex h-full flex-col bg-[#111827] text-gray-300 font-medium border-r border-gray-800">
      <div className={cn("flex h-14 items-center pt-4 pb-2", isCollapsed ? "justify-center px-2" : "justify-between px-5")}>
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-purple-400" />
            <span className="text-white font-bold text-sm tracking-wide">Admoji Admin</span>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-1 rounded hover:bg-white/10 transition-colors"
          data-testid="button-toggle-admin-sidebar"
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronLeft className="h-4 w-4 text-gray-400" />
          )}
        </button>
      </div>

      <div className={cn("flex-1 overflow-y-auto py-3 space-y-5", isCollapsed ? "px-2" : "px-3")}>
        {SECTIONS.map((section) => (
          <div key={section.label}>
            {!isCollapsed && (
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                {section.label}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavItemEl key={item.href} item={item} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={cn("border-t border-gray-800 py-3", isCollapsed ? "px-2" : "px-3")}>
        <Link
          href="/"
          className={cn(
            "flex items-center gap-2 py-2.5 rounded-lg text-sm text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors",
            isCollapsed ? "justify-center px-2" : "px-3"
          )}
          data-testid="link-back-to-app"
          title={isCollapsed ? "Back to app" : undefined}
        >
          <ArrowLeft className="h-4 w-4 flex-shrink-0" />
          {!isCollapsed && <span>Back to app</span>}
          {/* Build version label — same marker as the canvas sidebar footer
              (bump APP_VERSION in client/src/lib/version.ts on each push to
              confirm new code is live on production). */}
          {!isCollapsed && (
            <span
              data-testid="admin-app-version"
              title={`Build v${APP_VERSION}`}
              className="ml-auto font-mono text-[10px] text-gray-600 select-none tracking-wide"
            >
              v{APP_VERSION}
            </span>
          )}
        </Link>
      </div>
    </div>
  );
}
